import { apiRequest } from "./client";
import { normalizeListPayload } from "../utils/pagination";

export const makeSlug = (value = "") =>
  value.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const query = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const value = search.toString();
  return value ? `?${value}` : "";
};

const normalizePaginated = (payload, params = {}) =>
  normalizeListPayload(payload, { itemKeys: ["categories"], perPage: params.per_page });

export const asBoolean = (value, fallback = true) => {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "string") return !["0", "false", "no"].includes(value.toLowerCase());
  return !!value;
};

const isUploadedFile = (value) => typeof File !== "undefined" && value instanceof File;

const mediaUrl = (value) => {
  if (!value || isUploadedFile(value)) return "";
  if (typeof value === "string") return value === "[object File]" ? "" : value;
  if (typeof value !== "object") return "";
  return value.url || value.original_url || value.preview_url || value.path || value.file?.url || "";
};

export const categoryThumbnailUrl = (category) => {
  if (!category || typeof category !== "object") return "";
  return (
    mediaUrl(category.thumbnail_image) ||
    mediaUrl(category.thumbnail) ||
    mediaUrl(category.image) ||
    mediaUrl(category.thumbnail_image_url) ||
    mediaUrl(category.thumbnail_url) ||
    mediaUrl(category.image_url) ||
    mediaUrl(category.media?.[0])
  );
};

export const buildCreateCategoryPayload = ({
  name,
  name_zh = "",
  slug,
  status = true,
  sort_order = 0,
  show_in_navigation_menu = true,
  is_main_category = true,
  parent = null,
}) => ({
  name,
  name_zh,
  slug: slug || makeSlug(name),
  status: !!status,
  sort_order: Number(sort_order) || 0,
  show_in_navigation_menu: asBoolean(show_in_navigation_menu, true),
  is_main_category: asBoolean(is_main_category, true),
  parent: parent ?? null,
});

export const buildUpdateCategoryPayload = ({
  name,
  name_zh = "",
  slug,
  status = true,
  sort_order = 0,
  show_in_navigation_menu = true,
  is_main_category = true,
  parent = null,
  thumbnail_image,
}) => {
  const payload = {
    name,
    name_zh,
    slug,
    status: !!status,
    sort_order: Number(sort_order) || 0,
    show_in_navigation_menu: asBoolean(show_in_navigation_menu, true),
    is_main_category: asBoolean(is_main_category, true),
    parent: parent ?? null,
  };
  if (thumbnail_image === null) payload.thumbnail_image = null;
  return payload;
};

const appendCategoryFields = (form, category, builder) => {
  Object.entries(builder(category)).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      form.append(key, "");
      return;
    }
    form.append(key, typeof value === "boolean" ? (value ? "1" : "0") : String(value));
  });
};

const categoryFormData = (category, builder) => {
  const file = category.thumbnail_image;
  if (!file || !file.size) return null;
  const form = new FormData();
  appendCategoryFields(form, category, builder);
  const name = String(file.name || "thumbnail.jpg").replace(/[^\w.\-]+/g, "_");
  form.append("thumbnail_image", file, name || "thumbnail.jpg");
  return form;
};

export const fetchCategories = async (params = {}) => {
  const payload = await apiRequest(
    `/categories${query({
      page: 1,
      per_page: 20,
      sort_by: "sort_order",
      sort_order: "desc",
      ...params,
    })}`
  );
  return normalizePaginated(payload, params);
};

export const fetchCategory = async (id) => {
  const payload = await apiRequest(`/categories/${id}`);
  return payload?.data?.category ?? payload?.data ?? payload;
};

export const createCategory = async (category) => {
  const form = categoryFormData(category, buildCreateCategoryPayload);
  const payload = await apiRequest("/categories", {
    method: "POST",
    body: form || JSON.stringify(buildCreateCategoryPayload(category)),
  });
  return payload?.data?.category ?? payload?.data ?? payload;
};

export const updateCategory = async (id, category) => {
  const form = categoryFormData(category, buildUpdateCategoryPayload);
  if (form) {
    form.append("_method", "PUT");
    const payload = await apiRequest(`/categories/${id}`, { method: "POST", body: form });
    return payload?.data?.category ?? payload?.data ?? payload;
  }

  const payload = await apiRequest(`/categories/${id}`, {
    method: "PUT",
    body: JSON.stringify(buildUpdateCategoryPayload(category)),
  });
  return payload?.data?.category ?? payload?.data ?? payload;
};

export const deleteCategory = async (id) => {
  const payload = await apiRequest(`/categories/${id}`, { method: "DELETE" });
  return payload?.data ?? payload;
};
